import {
  Badge,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@mevaser/ui'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  ChevronsLeft,
  ChevronsRight,
  Globe,
  LogOut,
  Monitor,
  Moon,
  Search,
  ShoppingCart,
  Sun,
  User,
} from 'lucide-react'
import type * as React from 'react'
import { useEffect, useState } from 'react'
import { useAuth } from '../lib/auth.js'
import { visibleGroups } from '../lib/nav.js'
import { useNavCounts } from '../lib/nav-counts.js'
import { useOrderDraftCount } from '../lib/order-draft.js'
import { useTheme } from '../lib/theme.js'
import { SoftphoneCallBar } from './softphone/call-bar.js'
import { RingDevicesMenu } from './softphone/ring-devices-menu.js'
import { SoftphoneProvider } from './softphone/softphone-provider.js'
import { SupportWidget } from './support-widget.js'
import { SwNavigation } from './sw-navigation.js'
import { TicketAlerts } from './ticket-alerts.js'
import { TimeClockButton } from './time-clock-button.js'

const SIDEBAR_KEY = 'mv-sidebar-collapsed'

// Built statically with no runtime env available (see infra/README.md deploy
// steps), so this is a build-time constant rather than an env var.
const SITE_URL = import.meta.env.DEV ? 'http://localhost:3001' : 'https://mevasertech.net'

export function AppShell({
  children,
  onOpenCommandPalette,
}: {
  children: React.ReactNode
  onOpenCommandPalette: () => void
}) {
  const { staff, can, signOut } = useAuth()
  const { theme, setTheme } = useTheme()
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === 'true')
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const cartCount = useOrderDraftCount()
  const navCounts = useNavCounts()

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, String(collapsed))
  }, [collapsed])

  const groups = visibleGroups(can)

  return (
    <SoftphoneProvider>
      <div className="flex h-dvh overflow-hidden bg-background">
        <aside
          className={cn(
            'flex shrink-0 flex-col border-e border-border bg-sidebar transition-[width] duration-200',
            collapsed ? 'w-16' : 'w-64',
          )}
        >
          <div className="flex h-14 items-center gap-2.5 px-4">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <span className="text-sm font-bold">M</span>
            </div>
            {!collapsed ? (
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">Mevaser Tech</p>
                <p className="truncate text-2xs text-foreground-muted">Back office</p>
              </div>
            ) : null}
          </div>

          <nav className="flex-1 overflow-y-auto scrollbar-thin px-2 py-2" aria-label="Main">
            {groups.map((group) => (
              <div key={group.label} className="mb-4">
                {!collapsed ? (
                  <p className="px-2 pb-1 text-2xs font-medium uppercase tracking-wide text-foreground-subtle">
                    {group.label}
                  </p>
                ) : null}
                <ul className="space-y-0.5">
                  {group.items.map((item) => {
                    const active = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)
                    const waiting = item.badge ? (navCounts[item.badge] ?? 0) : 0
                    const waitingText = waiting > 99 ? '99+' : String(waiting)
                    return (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          title={
                            waiting > 0
                              ? `${item.label}, ${waitingText} waiting`
                              : collapsed
                                ? item.label
                                : undefined
                          }
                          className={cn(
                            'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
                            collapsed && 'justify-center',
                            active
                              ? 'bg-sidebar-active text-sidebar-active-foreground'
                              : 'text-sidebar-foreground hover:bg-surface-sunken hover:text-foreground',
                          )}
                          aria-current={active ? 'page' : undefined}
                        >
                          <span className="relative flex shrink-0">
                            <item.icon className="size-4" aria-hidden="true" />
                            {collapsed && waiting > 0 ? (
                              <span
                                aria-hidden="true"
                                className="absolute -top-1.5 -end-2 min-w-4 rounded-full bg-danger px-1 text-center text-2xs font-semibold leading-4 text-white"
                              >
                                {waitingText}
                              </span>
                            ) : null}
                          </span>
                          {!collapsed ? <span className="truncate">{item.label}</span> : null}
                          {!collapsed && waiting > 0 ? (
                            <span
                              aria-hidden="true"
                              className="ms-auto min-w-5 shrink-0 rounded-full bg-danger px-1.5 text-center text-2xs font-semibold leading-5 text-white"
                            >
                              {waitingText}
                            </span>
                          ) : null}
                          {waiting > 0 ? (
                            <span className="sr-only">{`${waitingText} waiting`}</span>
                          ) : null}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </nav>

          <div className="border-t border-border p-2">
            <a
              href={`${SITE_URL}/?staff=${encodeURIComponent(staff?.name ?? 'Staff')}`}
              title={collapsed ? 'View site' : undefined}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-surface-sunken hover:text-foreground',
                collapsed && 'justify-center',
              )}
            >
              <Globe className="size-4 shrink-0" aria-hidden="true" />
              {!collapsed ? <span className="truncate">View site</span> : null}
            </a>
            <Button
              variant="ghost"
              size="sm"
              className={cn('w-full', collapsed && 'justify-center px-0')}
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronsRight /> : <ChevronsLeft />}
              {!collapsed ? <span>Collapse</span> : null}
            </Button>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-4">
            <button
              type="button"
              onClick={onOpenCommandPalette}
              className="flex h-8 max-w-md flex-1 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-foreground-subtle transition-colors hover:border-border-strong"
            >
              <Search className="size-4" aria-hidden="true" />
              <span>Search orders, devices, customers…</span>
              <kbd className="ms-auto rounded border border-border bg-surface px-1.5 py-0.5 font-sans text-2xs text-foreground-muted">
                ⌘K
              </kbd>
            </button>

            <div className="ms-auto flex items-center gap-1">
              <RingDevicesMenu />
              {can('timeclock:punch') ? <TimeClockButton /> : null}
              {cartCount > 0 ? (
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  aria-label={`Sale in progress, ${cartCount} item${cartCount === 1 ? '' : 's'}`}
                >
                  <Link to="/orders/new" className="relative">
                    <ShoppingCart />
                    <Badge
                      tone="primary"
                      className="absolute -top-1 -end-1 min-w-4 justify-center px-1 py-0 text-2xs leading-4"
                    >
                      {cartCount}
                    </Badge>
                  </Link>
                </Button>
              ) : null}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Change theme">
                    {theme === 'dark' ? <Moon /> : theme === 'light' ? <Sun /> : <Monitor />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setTheme('light')}>
                    <Sun /> Light
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setTheme('dark')}>
                    <Moon /> Dark
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setTheme('system')}>
                    <Monitor /> System
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <User />
                    <span className="hidden max-w-32 truncate sm:inline">{staff?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-52">
                  <DropdownMenuLabel>{staff?.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings">
                      <User /> Your account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem destructive onSelect={() => void signOut()}>
                    <LogOut /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto scrollbar-thin">{children}</main>
        </div>

        {/* Renders no DOM. Here rather than in main.tsx so it exists only for a
          signed-in staff member looking at the back office, and never on the
          login screen. */}
        <SupportWidget />
        <TicketAlerts />
        <SwNavigation />
        <SoftphoneCallBar />
      </div>
    </SoftphoneProvider>
  )
}

/** Consistent page heading: title, optional description, optional actions. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description?: string
  actions?: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 pb-5">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="mt-0.5 text-sm text-foreground-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  )
}

export function Page({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto w-full max-w-[1600px] p-6">{children}</div>
}
